import { useState, useMemo } from 'react';
import septemberContext from '../septemberContext';

// Generate packing list based on destination, activities, and season
function generatePackingList(destination, activities, flight) {
  if (!destination) return [];

  const ctx = septemberContext[destination.city] || {};
  const tempHigh = destination.weather_high_f || 75;
  const actNames = (activities || []).map(a => (a.name + ' ' + (a.category || '')).toLowerCase());

  const items = [];
  const add = (category, item, reason) => items.push({ category, item, reason, checked: false });

  // ── Essentials ──
  add('Essentials', 'Passport (valid 6+ months)', 'Required for international travel');
  add('Essentials', 'Phone charger + adapter plug', destination.region?.includes('Europe') ? 'Type C/F plug for Europe' : 'Check plug type');
  add('Essentials', 'Travel insurance docs', 'Print or screenshot confirmation');
  add('Essentials', 'Copies of booking confirmations', 'Flights, hotel, activities');
  if (flight) {
    add('Essentials', 'Airline loyalty account info', `${flight.airline_program} account numbers for both travelers`);
  }

  // ── Clothing ──
  if (tempHigh >= 75) {
    add('Clothing', 'Light summer clothes', `It'll be ${tempHigh}°F`);
    add('Clothing', 'Shorts / skirts', 'Warm enough for daytime');
    add('Clothing', 'Sunglasses', 'September sun is strong');
    add('Clothing', 'Sun hat', 'Especially for outdoor activities');
  }
  if (tempHigh < 70) {
    add('Clothing', 'Light jacket or sweater', `Temps around ${tempHigh}°F`);
    add('Clothing', 'Long pants', 'For cooler evenings');
  }
  if (tempHigh < 60) {
    add('Clothing', 'Warm layers', `Can get down to ${tempHigh - 15}°F`);
    add('Clothing', 'Waterproof jacket', 'September can be rainy');
  }
  add('Clothing', 'Comfortable walking shoes', 'You\'ll walk a lot');
  add('Clothing', 'One nice outfit', 'For fancy dinners or going out');

  // ── Weather-specific ──
  if (ctx.warning && ctx.warning.toLowerCase().includes('rain')) {
    add('Weather', 'Rain jacket or compact umbrella', ctx.warning);
  }
  if (ctx.weatherVibe?.toLowerCase().includes('hot') || tempHigh > 82) {
    add('Weather', 'Sunscreen SPF 50+', 'September sun is no joke');
    add('Weather', 'Reusable water bottle', 'Stay hydrated');
  }

  // ── Activity-specific ──
  const hasBeach = actNames.some(n => n.includes('beach') || n.includes('swim') || n.includes('kayak') || n.includes('snorkel') || n.includes('boat') || n.includes('sail'));
  const hasHiking = actNames.some(n => n.includes('hike') || n.includes('path') || n.includes('trail') || n.includes('volcano'));
  const hasNightlife = actNames.some(n => n.includes('club') || n.includes('nightlife') || n.includes('bar crawl'));
  const hasSurfing = actNames.some(n => n.includes('surf'));
  const hasFoodTour = actNames.some(n => n.includes('food') || n.includes('tasting') || n.includes('cooking'));

  if (hasBeach) {
    add('Activities', 'Swimsuit (2)', 'Multiple beach/water days');
    add('Activities', 'Quick-dry towel', 'Lighter than hotel towels');
    add('Activities', 'Waterproof phone case', 'For boats, beaches, kayaking');
  }
  if (hasHiking) {
    add('Activities', 'Hiking shoes / sturdy sneakers', 'Proper footwear for trails');
    add('Activities', 'Daypack / small backpack', 'Water, snacks, sunscreen');
  }
  if (hasNightlife) {
    add('Activities', 'Going-out outfit', 'Some clubs have dress codes');
  }
  if (hasSurfing) {
    add('Activities', 'Rash guard', 'Sun protection in the water');
  }
  if (hasFoodTour) {
    add('Activities', 'Stretchy pants', "You're going to eat a lot.");
  }

  // ── Tech ──
  add('Tech', 'Camera / phone with storage', 'You\'ll take 1000+ photos');
  add('Tech', 'Portable charger', 'Long days out need backup power');

  // ── Health ──
  add('Health', 'Any prescriptions', 'Pack in carry-on, not checked bag');
  add('Health', 'Basic first aid (bandaids, ibuprofen)', 'For blisters and headaches');

  return items;
}

export default function PackingList({ destination, activities, flight }) {
  const items = useMemo(() => generatePackingList(destination, activities, flight), [destination, activities, flight]);
  const [checked, setChecked] = useState({});
  const [collapsed, setCollapsed] = useState({});

  if (items.length === 0) return null;

  const toggle = (idx) => setChecked(prev => ({ ...prev, [idx]: !prev[idx] }));
  const toggleCat = (cat) => setCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }));

  const categories = [...new Set(items.map(i => i.category))];
  const checkedCount = Object.values(checked).filter(Boolean).length;

  return (
    <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg text-white">Packing List</h3>
          <div className="text-xs text-brand-ink/55 mt-0.5">Auto-generated from your trip details. Check things off as you pack.</div>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-teal">{checkedCount}/{items.length}</div>
          <div className="text-[10px] text-brand-ink/55">packed</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-brand-ink/10 rounded-full overflow-hidden">
        <div className="h-full bg-teal rounded-full transition-all duration-500" style={{ width: `${(checkedCount / items.length) * 100}%` }} />
      </div>

      {categories.map(cat => {
        const catItems = items.map((item, idx) => ({ ...item, idx })).filter(i => i.category === cat);
        const catChecked = catItems.filter(i => checked[i.idx]).length;
        const isCollapsed = collapsed[cat];

        return (
          <div key={cat}>
            <button onClick={() => toggleCat(cat)} className="flex items-center justify-between w-full text-left py-2.5 min-h-[44px] group">
              <div className="flex items-center gap-2">
                <span className={`text-xs transition ${isCollapsed ? 'rotate-0' : 'rotate-90'} text-brand-ink/55`}>&rsaquo;</span>
                <span className="text-xs font-bold text-brand-ink/70 uppercase tracking-wider">{cat}</span>
              </div>
              <span className="text-[10px] text-brand-ink/45">{catChecked}/{catItems.length}</span>
            </button>
            {!isCollapsed && (
              <div className="ml-4 space-y-0.5">
                {catItems.map(item => (
                  <button
                    key={item.idx}
                    onClick={() => toggle(item.idx)}
                    className={`flex items-start gap-3 w-full text-left py-2.5 px-2 rounded-lg transition group min-h-[44px] ${checked[item.idx] ? 'opacity-50' : 'hover:bg-brand-ink/5'}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center text-[10px] mt-0.5 flex-shrink-0 transition ${
                      checked[item.idx] ? 'border-teal bg-teal text-navy' : 'border-brand-ink/20 group-hover:border-white/40'
                    }`}>
                      {checked[item.idx] && '✓'}
                    </div>
                    <div className="flex-1">
                      <div className={`text-sm ${checked[item.idx] ? 'line-through text-brand-ink/55' : 'text-brand-ink/80'}`}>{item.item}</div>
                      <div className="text-[10px] text-brand-ink/50">{item.reason}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
